import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors, ArrowLeft, Plus, X, FloppyDisk, Spinner, Tag,
  PencilSimple, Trash, CheckCircle, CircleDashed, MagnifyingGlass,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

// ==================== MODAL ====================
const ServicoModal = ({ servico, onClose, onSuccess }) => {
  const isEdit = !!servico;
  const [form, setForm] = useState({
    codigo: servico?.codigo || '',
    nome: servico?.nome || '',
    categoria: servico?.categoria || '',
    valor_base: servico ? String(servico.valor_base).replace('.', ',') : '',
    status: servico?.status || 'ativo',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch next code on create
  useEffect(() => {
    if (!isEdit && !form.codigo) {
      fetch(`${API}/servicos/next-codigo`).then(r => r.json()).then(d => {
        setForm(f => ({ ...f, codigo: d.codigo || '' }));
      }).catch(() => {});
    }
  }, [isEdit, form.codigo]);

  const handleSave = async () => {
    if (!form.nome.trim()) { setError('Nome e obrigatorio'); return; }
    const valor = parseFloat(String(form.valor_base).replace(',', '.')) || 0;
    setSaving(true); setError('');
    try {
      const url = isEdit ? `${API}/servicos/${servico.id}` : `${API}/servicos`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigo: form.codigo.trim(),
          nome: form.nome.trim(),
          categoria: form.categoria.trim(),
          valor_base: valor,
          status: form.status,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err?.detail || 'Erro ao salvar');
        return;
      }
      onSuccess();
    } catch (e) { setError('Erro de conexao'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="servico-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
              <Tag size={20} weight="bold" style={{ color: '#1B4332' }} />
            </div>
            <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
              {isEdit ? 'Editar Servico' : 'Novo Servico'}
            </h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Codigo</label>
              <input
                data-testid="input-codigo"
                type="text"
                value={form.codigo}
                onChange={(e) => setForm(f => ({ ...f, codigo: e.target.value }))}
                placeholder="SRV-001"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Status</label>
              <select
                data-testid="select-status"
                value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none"
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Nome do Servico *</label>
            <input
              data-testid="input-nome"
              type="text"
              value={form.nome}
              onChange={(e) => { setForm(f => ({ ...f, nome: e.target.value })); setError(''); }}
              placeholder="Corte, Step Curls, Transparencia..."
              className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Categoria</label>
              <input
                data-testid="input-categoria"
                type="text"
                value={form.categoria}
                onChange={(e) => setForm(f => ({ ...f, categoria: e.target.value }))}
                placeholder="Opcional"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Valor Base (R$)</label>
              <input
                data-testid="input-valor-base"
                type="text"
                value={form.valor_base}
                onChange={(e) => setForm(f => ({ ...f, valor_base: e.target.value }))}
                placeholder="0,00"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm text-right focus:outline-none"
              />
            </div>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A]">
              Cancelar
            </button>
            <button
              data-testid="btn-salvar-servico"
              onClick={handleSave}
              disabled={saving || !form.nome.trim()}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 hover:shadow-lg"
              style={{ backgroundColor: '#1B4332' }}
            >
              {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================
export const ServicosPage = () => {
  const navigate = useNavigate();
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');

  const fetchServicos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/servicos`);
      setServicos(await res.json());
    } catch { setServicos([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchServicos(); }, [fetchServicos]);

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Excluir servico "${nome}"?`)) return;
    await fetch(`${API}/servicos/${id}`, { method: 'DELETE' });
    fetchServicos();
  };

  const handleToggleStatus = async (srv) => {
    const novo = srv.status === 'ativo' ? 'inativo' : 'ativo';
    await fetch(`${API}/servicos/${srv.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...srv, status: novo }),
    });
    fetchServicos();
  };

  const filtered = servicos.filter(s => {
    if (filterStatus !== 'todos' && s.status !== filterStatus) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (s.nome || '').toLowerCase().includes(q) ||
             (s.codigo || '').toLowerCase().includes(q) ||
             (s.categoria || '').toLowerCase().includes(q);
    }
    return true;
  });

  const ativos = servicos.filter(s => s.status === 'ativo').length;
  const inativos = servicos.length - ativos;

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="servicos-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao')} data-testid="btn-back" className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" /><span>Voltar para Gestao</span>
          </button>
        </header>

        <div className="mb-4 animate-fade-in">
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">Servicos</span>
          </p>
        </div>

        <section className="mb-6 animate-fade-in-up">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
                <Tag size={28} weight="regular" style={{ color: '#1B4332' }} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="salonup-heading-lg">Servicos</h1>
                  <span className="salonup-badge">CATALOGO</span>
                </div>
                <p className="salonup-text-sm mt-1">Cadastre servicos, codigos e valores base usados nas comandas</p>
              </div>
            </div>
            <button
              data-testid="btn-novo-servico"
              onClick={() => { setEditing(null); setShowModal(true); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
              style={{ backgroundColor: '#1B4332' }}
            >
              <Plus size={16} weight="bold" />Novo Servico
            </button>
          </div>
        </section>

        {/* Resumo */}
        <section className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
          <div className="salonup-card p-4" data-testid="card-total">
            <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-1">Total</p>
            <p className="text-2xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{servicos.length}</p>
          </div>
          <div className="salonup-card p-4" data-testid="card-ativos">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#1B4332' }}>Ativos</p>
            <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit', color: '#1B4332' }}>{ativos}</p>
          </div>
          <div className="salonup-card p-4" data-testid="card-inativos">
            <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-1">Inativos</p>
            <p className="text-2xl font-semibold text-[#A1A1AA]" style={{ fontFamily: 'Outfit' }}>{inativos}</p>
          </div>
        </section>

        {/* Busca + filtro */}
        <section className="mb-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              data-testid="input-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por codigo, nome ou categoria..."
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            />
          </div>
          <div className="flex gap-1 p-1 bg-white border border-[#E4E4E7] rounded-xl">
            {['todos', 'ativo', 'inativo'].map(s => (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all ${filterStatus === s ? 'bg-[#1B4332] text-white' : 'text-[#71717A] hover:bg-[#F4F4F5]'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </section>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="salonup-card p-10 text-center max-w-md mx-auto animate-fade-in-up">
            <Tag size={36} weight="light" className="mx-auto mb-4 text-[#A1A1AA]" />
            <h3 className="text-lg font-semibold text-[#18181B] mb-2" style={{ fontFamily: 'Outfit' }}>
              {search ? 'Nenhum resultado' : 'Nenhum servico cadastrado'}
            </h3>
            <p className="text-sm text-[#71717A]">
              {search ? 'Ajuste sua busca ou filtros.' : 'Clique em "Novo Servico" para comecar.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 animate-fade-in-up" data-testid="servicos-list">
            {filtered.map(s => {
              const isAtivo = s.status === 'ativo';
              return (
                <div
                  key={s.id}
                  data-testid={`servico-${s.codigo}`}
                  className="salonup-card p-4 cursor-default hover:transform-none flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isAtivo ? '#1B433215' : '#F4F4F5' }}>
                    <Tag size={16} weight="bold" style={{ color: isAtivo ? '#1B4332' : '#A1A1AA' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{s.codigo}</span>
                      {!isAtivo && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-[#F4F4F5] text-[#A1A1AA]">Inativo</span>
                      )}
                      {s.categoria && (
                        <span className="text-[10px] text-[#71717A]">• {s.categoria}</span>
                      )}
                    </div>
                    <p className={`text-sm truncate ${isAtivo ? 'text-[#18181B]' : 'text-[#A1A1AA]'}`}>{s.nome}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: isAtivo ? '#1B4332' : '#A1A1AA' }}>
                      {formatCurrency(s.valor_base)}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      data-testid={`btn-toggle-${s.codigo}`}
                      onClick={() => handleToggleStatus(s)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#1B4332] hover:bg-[#F0FDF4] transition-all"
                      title={isAtivo ? 'Inativar' : 'Ativar'}
                    >
                      {isAtivo ? <CheckCircle size={16} weight="bold" /> : <CircleDashed size={16} weight="bold" />}
                    </button>
                    <button
                      data-testid={`btn-edit-${s.codigo}`}
                      onClick={() => { setEditing(s); setShowModal(true); }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#1B4332] hover:bg-[#F4F4F5] transition-all"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      data-testid={`btn-delete-${s.codigo}`}
                      onClick={() => handleDelete(s.id, s.nome)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <footer className="salonup-footer animate-fade-in mt-8">
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
        </footer>
      </div>

      {showModal && (
        <ServicoModal
          servico={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSuccess={() => { setShowModal(false); setEditing(null); fetchServicos(); }}
        />
      )}
    </div>
  );
};

export default ServicosPage;
